import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { InvoiceDisplayStatus } from 'anchor-scout-common/types';
import { FullPageModalAdditionalActions, useOverlayContext } from 'seagull';
import { DocumentCheckmark24, AttachFile, MoneyChange, MoneyReceive, RedoLiner24, SyncLiner24, Void, CopyLiner24, SendLiner24, Download } from 'seagull/icons';
import { getRefundActionConfig, allowResendAction, allowVoidAction, allowUnvoidAction, allowUnmarkPaidExternallyAction } from '~/business-logic/invoiceLogic';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useDownloadInvoicePdf } from '~/hooks/invoice/useDownloadInvoicePdf';
import { useCopyDirectLink } from '~/hooks/useCopyDirectLink';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { usePaidExternally } from '~/hooks/usePaidExternally';
import { useResendInvoice } from '~/hooks/useResendInvoice';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { invoiceModel, relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import { makeResumePaymentInvoiceAction, terminatedTooltip } from './FullPageInvoice.utils';
import { useCanResyncQbo } from './hooks/useCanResyncQbo';
import { useCanResyncXero } from './hooks/useCanResyncXero';
import { useResyncQbo } from './hooks/useResyncQbo';
import { useResyncXero } from './hooks/useResyncXero';
export const FullPageInvoiceAdditionalActionsTestIds = pathgen('FullPageInvoiceAdditionalActionsTestIds');
export default function FullPageInvoiceAdditionalActions(props) {
    const { invoice, relationship, isVendor, reloadInvoice, onStopPayment, onResumePayment, onVoidInvoice, onUnvoidInvoice, onUnmarkPaidExternally, onRequestRefund, onAttachFilesClick, isUploadingAttachments } = props;
    const { XERO_INTEGRATION, ATTACH_FILES } = useFeatureFlags();
    const { shouldShowMarkAsPaid, markAsPaid } = usePaidExternally();
    const { isResendingInvoice, resendInvoice } = useResendInvoice();
    const { isFetchingLink, copyDirectLink } = useCopyDirectLink();
    const { logBI } = useStoreActions();
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const isClientMember = !isVendor && isMemberUser;
    const { isDownloadingInvoicePdf, downloadInvoicePdf } = useDownloadInvoicePdf();
    const history = useHistory();
    const isTerminated = relationshipModel.isTerminated(relationship);
    const { showConfirmAction } = useOverlayContext();
    const refundActionConfig = getRefundActionConfig({
        invoice,
        isVendor
    });
    const isInvoiceDisputed = invoice.displayStatus === InvoiceDisplayStatus.PaymentDisputed;
    const showQboIntegration = invoice.integration.isSynced() && !invoice.integration.isRefundRecorded() && !isInvoiceDisputed;
    const showXeroIntegration = XERO_INTEGRATION && invoice.xeroIntegration.isPaymentRecorded() && !isInvoiceDisputed;
    const showResyncQboIntegration = showQboIntegration && isVendor;
    const showResyncXeroIntegration = showXeroIntegration && isVendor;
    const showAttachFilesIntegration = ATTACH_FILES && isVendor;
    const { canResyncQbo, refreshQbo } = useCanResyncQbo({
        enabled: !!showResyncQboIntegration,
        invoiceId: invoice.id
    });
    const { canResyncXero, refreshXero } = useCanResyncXero({
        enabled: !!showResyncXeroIntegration,
        invoiceId: invoice.id
    });
    const { resyncQbo } = useResyncQbo({
        onSettled: ()=>refreshQbo(),
        isPaymentRecorded: invoice.integration.isPaymentRecorded()
    });
    const { resyncXero } = useResyncXero({
        onSettled: ()=>refreshXero()
    });
    const disabledResyncTooltip = 'Paid invoices already included in a synced payout can’t be resynced';
    const isResyncQboIntegrationDisabled = isClientMember || !canResyncQbo;
    const isResyncXeroIntegrationDisabled = isClientMember || !canResyncXero;
    const handleResync = (param)=>{
        let { isPaymentRecorded, isInvoiceRecorded, agreementName, onConfirm } = param;
        let content;
        if (isPaymentRecorded && isInvoiceRecorded) {
            content = "A new invoice and payment will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous invoice and payment are first deleted from ").concat(agreementName, ". Please ensure they are removed before continuing.");
        } else if (isPaymentRecorded) {
            content = "A new payment will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous payment is first deleted from ").concat(agreementName, ". Please ensure it is removed before continuing.");
        } else if (isInvoiceRecorded) {
            content = "A new invoice will be created in ".concat(agreementName, ". The re-sync will only succeed if the previous invoice is first deleted from ").concat(agreementName, ". Please ensure it is removed before continuing.");
        } else {
            return;
        }
        showConfirmAction({
            title: "Re-sync to ".concat(agreementName),
            content,
            primaryButtonLabel: 'Re-sync',
            secondaryButtonLabel: 'Cancel',
            width: '610px',
            onConfirm
        });
    };
    const handleResyncQbo = ()=>{
        handleResync({
            isPaymentRecorded: invoice.integration.isPaymentRecorded(),
            isInvoiceRecorded: invoice.integration.isPaymentRecorded() || invoice.integration.isInvoiceRecorded(),
            agreementName: getAccountingDetails(Accounting.QBO).fullName,
            onConfirm: ()=>resyncQbo(invoice.id)
        });
    };
    const handleResyncXero = ()=>{
        const integration = invoice.xeroIntegration.getIntegration();
        handleResync({
            isPaymentRecorded: integration.isPaymentRecorded(),
            isInvoiceRecorded: false,
            agreementName: 'Xero',
            onConfirm: ()=>resyncXero(invoice.id)
        });
    };
    const inlineActions = compact([
        {
            text: 'Download PDF',
            testId: 'fpi-download-pdf',
            intent: Intent.InvoiceDownload,
            disabled: isDownloadingInvoicePdf || isClientMember || !invoice.pdfExists,
            tooltipTitle: invoice.pdfExists ? undefined : 'Generating PDF',
            iconStart: /*#__PURE__*/ _jsx(Download, {}),
            onClick: ()=>downloadInvoicePdf(invoice.id)
        }
    ]);
    const dropdownActions = compact([
        allowResendAction(invoiceModel.statusType(invoice), isVendor) && {
            text: 'Resend invoice',
            testId: 'fpi-resend-invoice',
            intent: Intent.InvoiceResend,
            disabled: isResendingInvoice || isClientMember,
            iconStart: /*#__PURE__*/ _jsx(SendLiner24, {}),
            onClick: ()=>resendInvoice(invoice).then(()=>reloadInvoice())
        },
        {
            text: 'Copy link',
            testId: 'fpi-copy-link',
            intent: Intent.InvoiceCopyLink,
            disabled: isFetchingLink || isClientMember,
            iconStart: /*#__PURE__*/ _jsx(CopyLiner24, {}),
            onClick: ()=>{
                logBI(14001);
                copyDirectLink(relationship.id, {
                    type: 'invoice',
                    id: invoice.id
                });
            }
        },
        {
            text: 'View agreement',
            testId: 'fpi-view-agreement',
            disabled: isClientMember,
            iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark24, {}),
            onClick: ()=>history.push(routes.agreement(relationshipModel.id(relationship)))
        },
        shouldShowMarkAsPaid(invoiceModel.statusType(invoice), isVendor) && {
            text: 'Mark as paid externally',
            testId: 'fpi-mark-paid-externally',
            intent: Intent.InvoiceMarkPaidExternally,
            disabled: isClientMember,
            iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
            onClick: ()=>markAsPaid(invoice, ()=>reloadInvoice())
        },
        refundActionConfig.showAction && {
            text: refundActionConfig.text,
            testId: 'fpi-request-refund',
            intent: refundActionConfig.intent,
            iconStart: /*#__PURE__*/ _jsx(MoneyChange, {}),
            onClick: onRequestRefund,
            tooltipTitle: refundActionConfig.tooltipTitle,
            disabled: refundActionConfig.disabled
        },
        showResyncQboIntegration && {
            text: "Re-sync to ".concat(getAccountingDetails(Accounting.QBO).fullName),
            testId: FullPageInvoiceAdditionalActionsTestIds.MoreActions.ResyncQboInvoices(),
            disabled: isResyncQboIntegrationDisabled,
            tooltipTitle: !canResyncQbo ? disabledResyncTooltip : undefined,
            iconStart: /*#__PURE__*/ _jsx(SyncLiner24, {}),
            onClick: handleResyncQbo
        },
        showResyncXeroIntegration && {
            text: 'Re-sync payment to Xero',
            testId: FullPageInvoiceAdditionalActionsTestIds.MoreActions.ResyncXeroInvoices(),
            disabled: isResyncXeroIntegrationDisabled,
            tooltipTitle: !canResyncXero ? disabledResyncTooltip : undefined,
            iconStart: /*#__PURE__*/ _jsx(SyncLiner24, {}),
            onClick: handleResyncXero
        },
        showAttachFilesIntegration && {
            text: 'Attach files',
            testId: FullPageInvoiceAdditionalActionsTestIds.MoreActions.AttachFiles(),
            iconStart: /*#__PURE__*/ _jsx(AttachFile, {}),
            onClick: onAttachFilesClick,
            disabled: isUploadingAttachments
        },
        allowVoidAction(invoiceModel.statusType(invoice), isVendor) && {
            text: 'Void invoice',
            testId: 'fpi-void-invoice',
            intent: Intent.InvoiceVoid,
            skin: 'error',
            iconStart: /*#__PURE__*/ _jsx(Void, {}),
            onClick: onVoidInvoice
        },
        allowUnvoidAction(invoice, isVendor) && {
            text: 'Unvoid invoice',
            testId: 'fpi-unvoid-invoice',
            intent: Intent.InvoiceUnvoid,
            iconStart: /*#__PURE__*/ _jsx(RedoLiner24, {}),
            onClick: onUnvoidInvoice
        },
        allowUnmarkPaidExternallyAction(invoice, isVendor) && {
            text: 'Unmark as paid externally',
            testId: 'fpi-unmark-paid-externally',
            intent: Intent.InvoiceUnmarkPaidExternally,
            iconStart: /*#__PURE__*/ _jsx(RedoLiner24, {}),
            onClick: onUnmarkPaidExternally
        },
        !isVendor && invoiceModel.isPending(invoice) && {
            text: 'Stop Payment',
            intent: Intent.InvoiceStopPayment,
            disabled: isTerminated,
            tooltipTitle: isTerminated ? terminatedTooltip : undefined,
            testId: 'fpi-stop-payment',
            skin: 'error',
            iconStart: /*#__PURE__*/ _jsx(Void, {}),
            onClick: onStopPayment
        },
        !isVendor && invoiceModel.isPaymentStopped(invoice) && _object_spread_props(_object_spread({}, makeResumePaymentInvoiceAction(isTerminated)), {
            testId: 'fpi-resume-payment',
            iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
            onClick: onResumePayment
        })
    ]);
    return /*#__PURE__*/ _jsx(FullPageModalAdditionalActions, {
        inlineActions: inlineActions,
        dropdownActions: dropdownActions,
        testId: FullPageInvoiceAdditionalActionsTestIds.FullPageModalAdditionalActions()
    });
}
